/**
 * /me/attendance/overtime — Session 3d PR 22.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { OvertimeRow } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyOvertimePage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-overtime", employeeId],
    queryFn: () => session3dApi.listMyOvertime(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<OvertimeRow>[] = [
    {
      headerKey: "me.overtime.col.date",
      label: "Date",
      render: (r) => <span dir="ltr" className="text-xs">{r.date}</span>,
    },
    {
      headerKey: "me.overtime.col.hours",
      label: "Hours",
      render: (r) => <span className="font-mono">{r.hours.toFixed(2)}</span>,
    },
    {
      headerKey: "me.overtime.col.premium",
      label: "Article 107 premium (SAR)",
      render: (r) => <span className="font-mono font-semibold">{r.article_107_premium_sar.toFixed(2)}</span>,
      traceText: (r) => r.trace_premium,
    },
    {
      headerKey: "me.overtime.col.state",
      label: "State",
      render: (r) => <span className="text-xs">{r.state}</span>,
    },
  ];

  return (
    <MeListPage<OvertimeRow>
      title={t("me.overtime.title", "My Overtime")}
      subtitle={t("me.overtime.subtitle", "Your OT history with Article 107 premium calculations.")}
      domain="overtime"
      khidmatPrefill={t("me.overtime.ask_khidmat", "How much OT have I earned this year?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.date}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.overtime.title", "My Overtime"),
        window_label: t("me.overtime.window", "this year"),
      }}
      emptyHeadline={t("me.overtime.empty_headline", "No overtime recorded.")}
      emptyBody={t("me.overtime.empty_body", "Khidmat: when you work beyond your shift, OT premium per Article 107 lands here.")}
    />
  );
}
